import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

type AnyObject = Record<keyof any, unknown>

type PropertySetter<T extends AnyObject, K extends keyof DeepReadonly<T>> = Dispatch<
  SetStateAction<DeepReadonly<T>[K]>
>

export type UseObjectState<T extends AnyObject> = [
  // The value itself
  DeepReadonly<T>,
  // The setter
  Dispatch<SetStateAction<DeepReadonly<T>>>,
  // Setter for a specific key
  <K extends keyof DeepReadonly<T>>(key: K) => PropertySetter<T, K>,
  // A ref to the value
  MutableRefObject<DeepReadonly<T>>,
]

/**
 * useObjectState hook for managing state with an object.
 *
 * This overload initializes the state with the provided object.
 *
 * @template T The type of the state object.
 * @param initialValue The initial state value.
 * @returns A tuple with the state object, a setter function for the whole state, a function to create setters for individual properties, and a ref to the state object.
 *
 * @example
 * const [user, setUser, setUserProperty, userRef] = useObjectState({ name: 'John', age: 30 });
 *
 * // Update the whole state
 * setUser({ name: 'Jane', age: 32 });
 *
 * // The function reference returned by setUserProperty is stable across renders.
 * const setUserName = setUserProperty('name');
 * setUserName('Doe');
 */
export default function useObjectState<T extends AnyObject>(initialValue: T): UseObjectState<T>

/**
 * useObjectState hook for managing state with an object.
 *
 * This overload initializes the state using a function that returns the initial state.
 *
 * @template Fn The type of the initialization function.
 * @param initFn The function to initialize the state.
 * @param initArgs Arguments passed to the initialization function.
 * @returns A tuple with the state object, a setter function for the whole state, a function to create setters for individual properties, and a ref to the state object.
 *
 * @example
 * const initUser = () => ({ name: 'John', age: 30 });
 * const [user, setUser, setUserProperty, userRef] = useObjectState(initUser);
 *
 * // Update the whole state
 * setUser({ name: 'Jane', age: 32 });
 *
 * // The function reference returned by setUserProperty is stable across renders.
 * const setUserName = setUserProperty('name');
 * setUserName('Doe');
 */
export default function useObjectState<Fn extends (...args: any[]) => AnyObject>(
  initFn: Fn,
  ...initArgs: Parameters<Fn>
): UseObjectState<ReturnType<Fn>>

export default function useObjectState<
  T extends AnyObject,
  Fn extends never | ((...args: any[]) => DeepReadonly<T>),
>(
  init: DeepReadonly<T> | Fn,
  ...initArgs: [Fn] extends [never] ? [] : Parameters<Fn>
): UseObjectState<T> {
  type RoT = DeepReadonly<T>

  // Initialize state using a function or directly with the provided value
  const [state, setState] = useState<RoT>(() =>
    typeof init === 'function' ? (init as Fn)(...initArgs) : init
  )

  // useRef maintains a stable reference to the state across renders. Useful for
  // accessing the current state in memoized functions like useCallback without
  // depending directly on the state variable.
  const stateRef = useRef(state)
  // Update the ref value whenever the state changes
  useEffect(() => {
    stateRef.current = state
  }, [state])

  const propertySetter = useCallback(
    (() => {
      // This object saves the setters for each property as they
      // get used so that the function references are stable
      const propertySettersMemo: {
        [K in keyof RoT]?: PropertySetter<T, K>
      } = {}

      return <K extends keyof RoT>(key: K): Dispatch<SetStateAction<RoT[K]>> => {
        // Check for existing memoized setter, else create a new one
        const memoizedSetter = propertySettersMemo[key]
        if (memoizedSetter) {
          return memoizedSetter
        }

        const newSetter: Dispatch<SetStateAction<RoT[K]>> = setter => {
          type P = RoT[K]

          setState(prevObj => {
            const newObject = { ...prevObj } as T

            // Handling functional updates vs direct value setting
            if (typeof setter === 'function') {
              // If the setter is a function, we need to pass the previous value
              // to it so that it can be used to compute the new value
              let prevPropertyValue: P = (newObject as RoT)[key]
              if (Array.isArray(prevPropertyValue)) {
                prevPropertyValue = [...(prevPropertyValue as any[])] as any as P
              } else if (typeof prevPropertyValue === 'object') {
                prevPropertyValue = { ...prevPropertyValue }
              }

              newObject[key] = setter(prevPropertyValue) as T[K]
            } else {
              // if the setter is not a function, we
              // can just use it as the new value
              newObject[key] = setter as T[K]
            }

            // Make sure this object is never mutated
            // because it would break React's rendering
            return Object.freeze(newObject) as RoT
          })
        }
        // Memoize property setters to provide stable functions across renders
        propertySettersMemo[key] = newSetter

        return newSetter
      }
    })(),
    [setState]
  )

  return [state, setState, propertySetter, stateRef]
}
